"use client";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Link from "@/components/ui/link";
import type { FooterQueryResult } from "@/lib/types";

// Static "Support" link appended to the bottom of the Company column.
// Not managed in Sanity - hardcoded so it ships with the route.
const SUPPORT_LINK = { label: "Support", href: "/support" };

// The "Company" footer column keeps a legacy _key from when it was titled
// "Investment Opportunities". Match on _key - it is stable and, unlike the
// title, is not stega-watermarked in draft/preview mode.
const COMPANY_GROUP_KEY = "investment-opportunities";

const isCompanyGroup = (key?: string) => key === COMPANY_GROUP_KEY;

// Type for links from the query result (with resolved references)
export type FooterLinkFromQuery = NonNullable<FooterQueryResult>["linkGroups"] extends
  | (infer T)[]
  | null
  ? T extends { links: (infer L)[] }
    ? L
    : never
  : never;

// Type for link groups from the query result
export type FooterLinkGroupFromQuery = NonNullable<FooterQueryResult>["linkGroups"] extends
  | (infer T)[]
  | null
  ? T
  : never;

export type ResolvedFooterLink = {
  _key?: string;
  label: string;
  href: string;
  isExternal: boolean;
  isFile: boolean;
};

export type ResolvedFooterLinkGroup = {
  _key?: string;
  title?: string;
  links?: ResolvedFooterLink[];
};

type FooterLinkGroupsProps = {
  linkGroups: ResolvedFooterLinkGroup[];
};

export function FooterLinkGroups({ linkGroups }: FooterLinkGroupsProps) {
  return (
    <>
      {/* Desktop: Side-by-side columns */}
      <div className="hidden flex-1 gap-6 lg:flex">
        {linkGroups.map((group) => {
          const links = group.links ?? [];
          return (
            <div key={group._key ?? group.title} className="flex flex-1 flex-col gap-5">
              {group.title ? (
                <h3 className="text-xl leading-7 font-medium text-white">{group.title}</h3>
              ) : null}
              <div className="flex flex-col gap-5">
                {links.map((link) => (
                  <Link
                    key={link._key ?? link.label}
                    href={link.href}
                    className="text-small leading-5 font-normal text-white transition-colors hover:text-emerald-400 md:text-xl md:leading-7"
                    target={link.isExternal || link.isFile ? "_blank" : undefined}
                    rel={link.isExternal || link.isFile ? "noreferrer noopener" : undefined}
                  >
                    {link.label}
                  </Link>
                ))}
                {isCompanyGroup(group._key) ? (
                  <Link
                    href={SUPPORT_LINK.href}
                    target="_blank"
                    className="text-small leading-5 font-normal text-white transition-colors hover:text-emerald-400 md:text-xl md:leading-7"
                  >
                    {SUPPORT_LINK.label}
                  </Link>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {/* Tablet & Mobile: Accordion */}
      <div className="flex flex-col gap-6 lg:hidden">
        <Accordion type="single" collapsible className="w-full">
          {linkGroups.map((group, index) => {
            const links = group.links ?? [];
            const value = group._key ?? group.title ?? `group-${index}`;

            return (
              <div key={group._key ?? group.title} className="w-full">
                <div className="h-px border-t border-white" />
                <AccordionItem value={value} className="border-0">
                  <AccordionTrigger className="py-padding-element-lg flex w-full items-center justify-center gap-2.5 px-0 text-lg leading-5 font-normal text-zinc-100 hover:no-underline md:text-2xl md:leading-9 [&>svg]:h-8 [&>svg]:w-8 [&>svg]:shrink-0">
                    <span className="flex-1 text-left">{group.title}</span>
                  </AccordionTrigger>
                  <AccordionContent className="pt-4 pb-0">
                    <div className="flex flex-col gap-4">
                      {links.map((link) => (
                        <a
                          key={link._key ?? link.label}
                          href={link.href}
                          className="text-xl leading-7 font-normal text-white underline transition-colors hover:text-emerald-400"
                          target={link.isExternal || link.isFile ? "_blank" : undefined}
                          rel={link.isExternal || link.isFile ? "noreferrer noopener" : undefined}
                        >
                          {link.label}
                        </a>
                      ))}
                      {isCompanyGroup(group._key) ? (
                        <a
                          href={SUPPORT_LINK.href}
                          className="text-xl leading-7 font-normal text-white underline transition-colors hover:text-emerald-400"
                        >
                          {SUPPORT_LINK.label}
                        </a>
                      ) : null}
                    </div>
                  </AccordionContent>
                </AccordionItem>
                {index === linkGroups.length - 1 && <div className="h-px border-t border-white" />}
              </div>
            );
          })}
        </Accordion>
      </div>
    </>
  );
}
